import type { LucideIcon } from 'lucide-react'
import { Label } from '@klear/ui/components/label'
import { Combobox } from '@klear/ui/components/combobox'
import type { ComboboxOption } from '@klear/ui/components/combobox'
import { useFormContext } from 'react-hook-form'
import get from 'lodash/get'
import { cn } from '@klear/ui/lib/utils'
import { useCallback, useMemo } from 'react'

interface FormComboboxProps<T = string> {
  label: string
  name: string
  placeholder?: string
  options: ComboboxOption<T>[]
  icon?: LucideIcon
  disabled?: boolean
  error?: { message?: string }
  isEditing?: boolean
  valueType?: 'string' | 'number' | 'object'
  className?: string
  helperText?: string
  required?: boolean
  nullable?: boolean
  onChange?: (value: T | '' | null) => void
}

export default function FormCombobox<T extends string | number | object>({
  label,
  name,
  options,
  icon: Icon,
  disabled,
  isEditing = true,
  placeholder,
  valueType = 'string',
  className,
  helperText,
  required = false,
  nullable = false,
  onChange
}: FormComboboxProps<T>) {
  const {
    watch,
    setValue,
    clearErrors,
    formState: { errors }
  } = useFormContext()
  const watchedValue = (watch(name) as T | undefined | null | '') ?? ''

  const fieldError = useMemo(() => get(errors, name), [errors, name])
  const errorMessage = get(fieldError, 'message')

  const handleChange = useCallback(
    (newValue: T | '') => {
      const writeValue = nullable && newValue === '' ? null : newValue
      setValue(name, writeValue, { shouldValidate: true, shouldDirty: true })
      clearErrors(name)
      onChange?.(writeValue)
    },
    [clearErrors, name, nullable, onChange, setValue]
  )

  return (
    <div className={cn('space-y-2', className)}>
      <Label htmlFor={name} className="flex items-center gap-2">
        {Icon && <Icon className="h-4 w-4" />}
        {label}
        {required ? <span className="text-destructive">*</span> : null}
      </Label>
      <Combobox<T>
        value={watchedValue}
        disabled={disabled || !isEditing}
        options={options}
        placeholder={placeholder}
        onChange={handleChange}
        error={fieldError}
        valueType={valueType}
        className="w-full"
      />
      {errorMessage && <p className="text-sm text-red-500">{errorMessage.toString()}</p>}
      {helperText && <p className="text-sm text-muted-foreground">{helperText}</p>}
    </div>
  )
}
